import * as Yup from "yup";
import moment from "moment/moment";

const nameSchema = {
  firstName: Yup.string()
    .matches(/^[A-Za-z\s'-]+$/, "First Name can contain letters, spaces, apostrophes and hyphens only")
    .max(25, "First Name cannot be more than 25 characters")
    .required("First Name is required"),
  lastName: Yup.string()
    .matches(/^[A-Za-z\s'-]+$/, "Last Name can contain letters, spaces, apostrophes and hyphens only")
    .max(25, "Last Name cannot be more than 25 characters")
    .required("Last Name is required"),
};

const emailSchema = {
  email: Yup.string()
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address")
    .required("Email is required"),
};

// Require: >= 8 chars, at least one lowercase, one uppercase, one number, and one special char (any punctuation)
// Accept longer passwords and a broad set of special characters (e.g., # _ - . , etc.)
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const passwordSchema = {
  password: Yup.string()
    .min(8, "Password must have at least 8 characters")
    .matches(
      passwordRegex,
      "Password must contain at least 8 characters, one uppercase, one lowercase, one number and one special character"
    )
    .required("Password is required"),
};

const loginPasswordSchema = {
  password: Yup.string()
    .required("Password is required"),
};

const dateTimeSchema = {
  startDate: Yup.date().required("Start date is required"),
  startTime: Yup.date().required("Start time is required"),
  endDate: Yup.date()
    .min(
      Yup.ref("startDate"),
      "End date should be later than or equal to start date"
    )
    .required("End date is required"),
  endTime: Yup.date()
    .test(
      "min_end_time",
      "End time must be after start time or can wrap past midnight",
      function (value) {
        const { startDate, endDate, startTime, endTime } = this.parent;

        const start_date = moment(startDate).format("YYYY-MM-DD");
        const end_date = moment(endDate).format("YYYY-MM-DD");
        const start_time = moment(startTime).format("HH:mm");
        const end_time = moment(endTime).format("HH:mm");

        if (start_date === end_date) {
          const start = moment(start_time, "HH:mm");
          const end = moment(end_time, "HH:mm");
          // Allow same-day increasing times OR overnight windows (end before start)
          if (end.isAfter(start)) return true; // same-day forward
          if (end.isBefore(start)) return true; // wraps past midnight
          return false; // equal times not allowed
        } else {
          return true;
        }
      }
    )
    .required("End time is required"),
};

export const signUpSchema = Yup.object().shape({
  ...nameSchema,
  ...emailSchema,
  ...passwordSchema,
});

export const fundraiserSignUpSchema = Yup.object().shape({
  ...nameSchema,
  ...emailSchema,
  ...passwordSchema,
  category: Yup.string().required("Category is required"),
});

export const loginSchema = Yup.object().shape({
  ...emailSchema,
  ...loginPasswordSchema,
});

export const forgetPasswordSchema = Yup.object().shape({
  ...emailSchema,
});

export const invitationSchema = Yup.object().shape({
  ...nameSchema,
  ...emailSchema,
  businessName: Yup.string()
    .max(25, "Business Name cannot be more than 25 characters")
    .required("Business Name is required"),
  businessAddress: Yup.string()
    .required("Business Address is required"),
  websiteUrl: Yup.string()
    .url("Please enter a valid URL")
    .required("Website URL is required"),
  personName: Yup.string()
    .matches(/^[A-Za-z0-9\s'-]+$/, "Contact Person Name can contain letters, numbers, spaces, apostrophes and hyphens only")
    .max(25, "Contact Person Name cannot be more than 25 characters")
    .required("Contact Person Name is required"),
  personContact: Yup.string()
    .matches(/^\+?\d{8,15}$/, "Invalid contact number")
    .required("Contact Person Number is required"),
  aboutBusiness: Yup.string()
    .required("Business Description is required")
    .max(500, "Business Description cannot be more than 500 characters"),
  // Make logo optional for admin invitation flow
  logo: Yup.string().nullable().notRequired(),
});

export const categorySchema = Yup.object({
  category: Yup.string().required("Category is required"),
});

export const subCategorySchema = Yup.object({
  subCategory: Yup.array()
    .of(Yup.string().required("Sub Categories are required"))
    .min(1, "Atleast Select one Sub Category"),
});

export const offerCreationSchema = (role) => {
  const isMerchant = role === "merchant" || role === "MERCHANT";

  return Yup.object().shape({
    title: Yup.string()
      .required("Offer title is required")
      .max(100, "Offer Title cannot be more than 100 characters"),
    offerType: Yup.string().required("Offer Type Selection is required"),
    offerDiscountType: Yup.string().required(
      "Offer Discount Type Selection is required"
    ),
    description: Yup.string()
      .nullable()
      .test("is-required-for-merchant", "Offer Description is required", (value) => {
        if (isMerchant) return !!value && value.trim().length > 0;
        return true;
      })
      .test(
        "no-offensive-words",
        "Description contains inappropriate language",
        (value) => {
          if (!value) return true;
          // Expand list with common offensive terms. Check as whole words to reduce false positives.
          const banned = [
            "asshole", "bastard", "bitch", "bloody", "damn", "fuck", "fucker", "fucking", "motherfucker", "shit", "shitty", "dick", "prick", "cunt",
            "racist", "sexist", "hate", "hate speech"
          ];
          const normalized = ` ${value.toLowerCase()} `;
          return !banned.some((w) => normalized.includes(` ${w} `));
        }
      ),
    discountRange: Yup.number().when("offerDiscountType", {
      is: "DISCOUNTED_PERCENT",
      then: () =>
        Yup.number()
          .transform((value) => (isNaN(value) ? 0 : value))
          .min(0, "Discount must be at least 0%")
          .max(100, "Discount cannot exceed 100%")
          .required("Range is required"),
      otherwise: () => Yup.number().nullable(),
    }),
    fixedDollarAmount: Yup.number().when("offerDiscountType", {
      is: "FIXED_DOLLAR",
      then: () => Yup.number().moreThan(0, "Fixed Dollar Amount must be greater than 0").max(10000, "Fixed Dollar Amount cannot exceed $10,000").required("Fixed Dollar Amount is required"),
      otherwise: () => Yup.number().nullable(),
    }),
    thumbnailImage: Yup.string().required("At least one thumbnail image is required"),
    previewImages: Yup.array()
      .default([])
      .transform((value) => {
        return value || [];
      })
      .of(
        Yup.string()
          .nullable()
          .matches(
            /\.(jpg|jpeg|png|gif|webp)$/i,
            "Invalid image format. Allowed formats: jpg, jpeg, png, gif, webp"
          )
      )
      .min(1, "At least one preview image is required")
      .max(4, "Maximum 4 preview images are allowed"),
    websiteURL: Yup.string().when("offerType", {
      is: "ONLINE_OFFER",
      then: () =>
        Yup.string()
          .matches(
            /^(https?:\/\/)?(www\.)?[\w-]+(\.[\w-]+)+.*$/,
            "Invalid website URL"
          )
          .required("Website URL is required"),
      otherwise: () => Yup.string().nullable(),
    }),
    offerCodes: Yup.array().when(["offerType", "onlineOption"], {
      is: (offerType, onlineOption) =>
        offerType === "ONLINE_OFFER" && onlineOption === "ONLINE_WITH_CODE",
      then: () =>
        Yup.array()
          .transform((value, originalValue) => {
            if (typeof originalValue === "string") {
              return originalValue
                .split(",")
                .map((code) => code.trim())
                .filter((code) => code.length > 0);
            }
            return value;
          })
          .of(
            Yup.string()
              .matches(/^[a-zA-Z0-9]{6}$/, "Each code must be exactly 6 alphanumeric characters")
          )
          .min(1, "At least one code is required"),
      otherwise: () => Yup.array(),
    }),
    document: Yup.string(),
  });
};

export const customizeOfferSchema = Yup.object({
  // ruleAndRegulation: Yup.string().required("Select exactly one rule."),
  offerTimeCheck: Yup.boolean().oneOf(
    [true, false],
    "You must add all the Times"
  ),
  ...dateTimeSchema,
  days: Yup.array()
    .of(Yup.string().required("Days are required"))
    .min(1, "At least select one Day!"),
});

export const rulesOfferSchema = Yup.object({
  ruleAndRegulation: Yup.array()
    .of(Yup.string().required("Rules are required"))
    .min(1, "Please select at least one Rule and Regulation"),
});

export const franchiseOfferSchema = Yup.object().shape({
  franchiseName: Yup.string()
    .max(50, "Franchise Name cannot be more than 25 characters")
    .required("Store Name is required"),
  ...emailSchema,
  contact: Yup.string().required("Contact is required"),
  location: Yup.string().required("Location is required"),
  timeSet: Yup.boolean().oneOf([false], "You must select at least one day"),
  timeData: Yup.array().of(
    Yup.object().shape({
      day: Yup.string().required(""),
      openTime: Yup.date().when("isSelected", {
        is: true,
        then: () => Yup.date().required("Open Time is required"),
        otherwise: () => Yup.date(), // No validation when isSelected is false
      }),
      closeTime: Yup.date().when("isSelected", {
        is: true, // Check both isSelected and openTime
        then: () =>
          Yup.date()
            .test(
              "min_close_time",
              "Close time must be later than start time or can wrap past midnight",
              function (value) {
                const { openTime, closeTime } = this.parent;
                const start_time = moment(openTime).format("HH:mm");
                const end_time = moment(closeTime).format("HH:mm");

                // Allow same-day increasing times OR overnight windows (end before start)
                if (moment(end_time, "HH:mm").isAfter(moment(start_time, "HH:mm"))) return true; // same-day forward
                if (moment(end_time, "HH:mm").isBefore(moment(start_time, "HH:mm"))) return true; // wraps past midnight
                return false; // equal times not allowed
              }
            )
            .required("Close time is required"),
        otherwise: () => Yup.date(),
      }),
      isSelected: Yup.boolean().oneOf([true, false], ""),
    })
  ),
});

export const uploadOfferSchema = Yup.object().shape({
  document: Yup.string().required("Excel document is required"),
});

export const locationOfferSchema = (allStores = false) =>
  Yup.object({
    location: allStores
      ? Yup.array().of(Yup.string()) // No validation when allStores is true
      : Yup.array()
        .of(Yup.string().required("Locations are required"))
        .min(1, "Atleast Select one Location"),
  });

export const adminOfferSchema = Yup.object({
  title: Yup.string()
    .required("Offer title is required")
    .max(25, "Offer Title cannot be more than 25 characters"),
  category: Yup.string().required("Category type is required"),
  subCategory: Yup.string().required("Sub Category is required"),
  ...dateTimeSchema,
  offerDescription: Yup.string()
    .required("Offer Description is required")
    .max(500, "Offer Description cannot be more than 500 characters"),
  ruleAndRegulation: Yup.string()
    .required("Rules are required")
    .max(500, "Rules and regulations cannot be more than 500 characters"),
  range: Yup.number().min(0).max(100).required("Range is required"),
  businessName: Yup.string().required("Business name is required"),
  franchise: Yup.array().required("Franchise is required"),
  document: Yup.string().required("Image document is required"),
});

export const merchantOfferSchema = Yup.object({
  title: Yup.string()
    .required("Offer title is required")
    .max(25, "Offer Title cannot be more than 25 characters"),
  category: Yup.string().required("Category type is required"),
  subCategory: Yup.string().required("Sub Category is required"),
  ...dateTimeSchema,
  offerDescription: Yup.string()
    .required("Offer Description is required")
    .max(500, "Offer Description cannot be more than 500 characters"),
  ruleAndRegulation: Yup.string()
    .required("Rules are required")
    .max(500, "Rules and regulations cannot be more than 500 characters"),
  range: Yup.number().min(0).max(100).required("Range is required"),
  document: Yup.string().required("Image document is required"),
});

export const assetsSchema = Yup.object({
  title: Yup.string().required("Asset Title is required"),
  assetDescription: Yup.string().required("Asset Description is required"),
  selectedFundraisers: Yup.array()
    .required("Fundraisers are required")
    .min(1, "There must be at least one fundraiser"),
  // Relaxed: allow either legacy document or at least one image upload handled in UI
  document: Yup.string().nullable(),
});

export const franchiseSchema = Yup.object().shape({
  franchiseName: Yup.string()
    .max(50, "Franchise Name cannot be more than 25 characters")
    .required("Franchise Name is required"),
  ...emailSchema,
  contact: Yup.string().required("Contact is required"),
  address: Yup.string().required("Location is required"),
  timeSet: Yup.boolean().oneOf([false], "You must select at least one day"),
  timeData: Yup.array().of(
    Yup.object().shape({
      day: Yup.string().required(""),
      openTime: Yup.date().when("isSelected", {
        is: true,
        then: () => Yup.date().required("Open Time is required"),
        otherwise: () => Yup.date(), // No validation when isSelected is false
      }),
      closeTime: Yup.date().when("isSelected", {
        is: true, // Check both isSelected and openTime
        then: () =>
          Yup.date()
            .test(
              "min_close_time",
              "Close time must be later than start time or can wrap past midnight",
              function (value) {
                const { openTime, closeTime } = this.parent;
                const start_time = moment(openTime).format("HH:mm");
                const end_time = moment(closeTime).format("HH:mm");

                // Allow same-day increasing times OR overnight windows (end before start)
                if (moment(end_time, "HH:mm").isAfter(moment(start_time, "HH:mm"))) return true; // same-day forward
                if (moment(end_time, "HH:mm").isBefore(moment(start_time, "HH:mm"))) return true; // wraps past midnight
                return false; // equal times not allowed
              }
            )
            .required("Close time is required"),
        otherwise: () => Yup.date(),
      }),
      isSelected: Yup.boolean().oneOf([true, false], ""),
    })
  ),
});

export const bankSchema = Yup.object({
  accountTitle: Yup.string().required("Account Title is required"),
  bankName: Yup.string().required("Bank Name is required"),
  branchName: Yup.string().required("Branch Name is required"),
  branchCode: Yup.number().required("Branch Code is required"),
  accountNo: Yup.string().required("Account Number is required"),
});

export const reReferSchema = Yup.object().shape({
  merchantName: Yup.string().nullable().notRequired(),
  ...emailSchema,
  contactPersonNumber: Yup.string().nullable().notRequired(),
  message: Yup.string().required("Message is required"),
});

export const referSchema = Yup.object().shape({
  merchantName: Yup.string().nullable().notRequired(),
  ...emailSchema,
  contactPersonNumber: Yup.string()
    .matches(/^\+?\d{8,15}$/, "Invalid contact number")
    .required("Contact person number is required"),
  website: Yup.string()
    .matches(
      /^(https?:\/\/)?(www\.)?[\w-]+(\.[\w-]+)+.*$/,
      "Invalid website URL"
    )
    .nullable()
    .notRequired(),
  message: Yup.string().required("Message is required"),
  // business: Yup.string().required("Business is required"),
  // location: Yup.string().required("Location is required"),
  // country: Yup.string().required("Country is required"),
  // state: Yup.string().required("State is required"),
  // city: Yup.string().required("City is required"),
  // postCode: Yup.string().required("Post Code is required"),
});

export const fundraiserAssetsSchema = Yup.object({
  sale: Yup.string().required("Sale is required"),
});

export const businessProfileSchema = Yup.object().shape({
  business: Yup.string()
    .max(25, "Business Name cannot be more than 25 characters")
    .required("Business Name is required"),
  abn: Yup.string()
    .matches(/^[0-9]{2} *[0-9]{3} *[0-9]{3} *[0-9]{3}$/, "Invalid ABN format")
    .required("ABN is required"),
  location: Yup.string().required("Location is required"),
  websiteUrl: Yup.string()
    .matches(
      /^(https?:\/\/)?(www\.)?[\w-]+(\.[\w-]+)+.*$/,
      "Invalid website URL"
    )
    .required("Website URL is required"),
  merchantCategory: Yup.array().required("Merchant Category is required"),
  primaryFirstName: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "First Name can only contain letters and spaces")
    .max(25, "First Name cannot be more than 25 characters")
    .required("First Name is required"),
  primaryLastName: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "Last Name can only contain letters and spaces")
    .max(25, "Last Name cannot be more than 25 characters")
    .required("Last Name is required"),
  primaryEmail: Yup.string()
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address")
    .required("Email is required"),
  primaryContact: Yup.string().required("Contact is required"),
  marketFirstName: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "First Name can only contain letters and spaces")
    .max(25, "First Name cannot be more than 25 characters")
    .required("First Name is required"),
  marketLastName: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "Last Name can only contain letters and spaces")
    .max(25, "Last Name cannot be more than 25 characters")
    .required("Last Name is required"),
  marketEmail: Yup.string()
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address")
    .required("Email is required"),
  marketContact: Yup.string().required("Contact is required"),
});

export const moreInfoSchema = Yup.object().shape({
  aboutBusiness: Yup.string().required("Business Detail is required"),
  physicalStore: Yup.string().required("Please select an option"),
  timeSet: Yup.boolean().oneOf([false], "You must select at least one day"),
  timeData: Yup.array().of(
    Yup.object().shape({
      day: Yup.string().required(""),
      openTime: Yup.date().when("isSelected", {
        is: true,
        then: () => Yup.date().required("Open Time is required"),
        otherwise: () => Yup.date(), // No validation when isSelected is false
      }),
      closeTime: Yup.date().when("isSelected", {
        is: true, // Check both isSelected and openTime
        then: () =>
          Yup.date()
            .test(
              "min_close_time",
              "Close time must be later than start time or can wrap past midnight",
              function (value) {
                const { openTime, closeTime } = this.parent;
                const start_time = moment(openTime).format("HH:mm");
                const end_time = moment(closeTime).format("HH:mm");

                // Allow same-day increasing times OR overnight windows (end before start)
                if (moment(end_time, "HH:mm").isAfter(moment(start_time, "HH:mm"))) return true; // same-day forward
                if (moment(end_time, "HH:mm").isBefore(moment(start_time, "HH:mm"))) return true; // wraps past midnight
                return false; // equal times not allowed
              }
            )
            .required("Close time is required"),
        otherwise: () => Yup.date(),
      }),
      isSelected: Yup.boolean().oneOf([true, false], ""),
    })
  ),
});

export const submitInfoSchema = Yup.object().shape({
  authCheckBox: Yup.boolean().oneOf([true], "You must accept this agreement"),
  termsCheckbox: Yup.boolean().oneOf([true], "You must accept this agreement"),
  privacyCheckbox: Yup.boolean().oneOf(
    [true],
    "You must accept privacy policy"
  ),
});

export const subscriptionSchema = Yup.object({
  category: Yup.string().required("Fundraiser Category is required"),
  fundraiser: Yup.string().required("Fundraiser is required"),
});

export const fundraiserSchema = Yup.object({});

export const membershipActivationSchema = Yup.object().shape({
  ...emailSchema,
  // confirmEmail: Yup.string()
  //   .oneOf([Yup.ref("email"), null], "Emails must match")
  //   .required("Email Confirmation is required"),
});

export const memberSetUpSchema = Yup.object().shape({
  ...nameSchema,
  ...emailSchema,
  phone: Yup.string().required("Phone Number is required"),
  age: Yup.number()
    .max(100, "Age must be at most 100")
    .required("Age is required"),
  location: Yup.string().required("Location is required"),
  ...passwordSchema,
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Password Confirmation is required"),
  club: Yup.string().max(
    25,
    "Fundraiser club name cannot be more than 25 characters"
  ),
  presidentEmail: Yup.string().matches(
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    "Invalid email address"
  ),
});

// Relaxed schema for social sign-up: password fields are optional/hidden
export const memberSetUpSocialSchema = Yup.object().shape({
  ...nameSchema,
  ...emailSchema,
  phone: Yup.string().required("Phone Number is required"),
  age: Yup.number()
    .max(100, "Age must be at most 100")
    .required("Age is required"),
  location: Yup.string().required("Location is required"),
  club: Yup.string().max(
    25,
    "Fundraiser club name cannot be more than 25 characters"
  ),
  presidentEmail: Yup.string().matches(
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    "Invalid email address"
  ),
});

export const existingLoginSchema = Yup.object().shape({
  ...loginPasswordSchema,
});

export const otpSchema = Yup.object().shape({
  otp: Yup.string().required("OTP is required"),
});

export const resetPasswordSchema = Yup.object().shape({
  ...passwordSchema,
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Password Confirmation is required"),
});

export const updatePasswordSchema = Yup.object().shape({
  currentPassword: Yup.string().required("Current Password is required"),
  newPassword: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/,
      "Password must contain at least 8 characters, one uppercase, one lowercase, one number and one special character"
    )
    .required("New Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .required("Password Confirmation is required"),
});

export const withdrawSchema = Yup.object().shape({
  amount: Yup.number().required("Amount to withdraw is required"),
  bankCheckBox: Yup.boolean().oneOf([true], "You must select a bank"),
});

export const gettingStartedSchema = Yup.object().shape({
  businessName: Yup.string()
    .matches(/^[\p{L}0-9\s&'().,\-@#$%!?/:;+=_*[\]{}|~`"\\]+$/u, "Business Name can contain letters, numbers, spaces, and special characters")
    .max(70, "Business Name cannot be more than 70 characters")
    .required("Business Name is required"),
  businessAddress: Yup.string().required("Business Address is required"),
  // Make logo optional on getting-started
  logo: Yup.string().nullable().notRequired(),
  // websiteUrl: Yup.string()
  //   .url("Invalid URL format")
  //   .required("Website URL is required"),
  // websiteUrl: Yup.string()
  //   .matches(
  //     /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+)\.com(\.[a-zA-Z]+)*$/,
  //     "Invalid URL format"
  //   )
  //   .required("Website URL is required"),
  websiteUrl: Yup.string()
    .matches(
      /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}(\/[^\s]*)?$/,
      "Invalid URL format"
    )
    .required("Website URL is required"),
  businessCategory: Yup.array()
    .of(Yup.string().required("Business Categories are required"))
    .min(1, "Atleast Select one Business Category"),
  personName: Yup.string()
    .matches(/^[A-Za-z0-9\s]+$/, "Contact Person Name can contain letters, numbers, and spaces")
    .max(50, "Contact Person Name cannot be more than 50 characters")
    .required("Contact Person Name is required"),
  ...emailSchema,
  personContact: Yup.string().required("Contact is required"),
  aboutBusiness: Yup.string()
    .required("Business Information is required")
    .max(500, "Business Information cannot be more than 500 characters"),
  password: Yup.string(),
});

// New validation rules added to the schema.
export const createMerchantAccountSchema = Yup.object().shape({
  ...nameSchema,
  ...passwordSchema,
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Password Confirmation is required"),
});

// Fundraiser account creation schema
export const createFundraiserAccountSchema = Yup.object().shape({
  ...nameSchema,
  ...passwordSchema,
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Password Confirmation is required"),
  organizationName: Yup.string()
    .max(100, "Organisation Name cannot be more than 100 characters")
    .required("Organisation Name is required"),
  abn: Yup.string()
    .matches(/^[0-9]{11}$/, "ABN must be an 11-digit number")
    .required("ABN is required"),
  accountEmail: Yup.string()
    .email("Invalid email address")
    .required("Account Email is required"),
  isRegisteredCharity: Yup.boolean().optional(),
  charityRegistrationNumber: Yup.string().when("isRegisteredCharity", {
    is: true,
    then: (schema) =>
      schema
        .required("Charity Registration Number is required")
        .max(50, "Registration Number is too long"),
    otherwise: (schema) => schema.optional(),
  }),
  isRegisteredToGST: Yup.boolean().optional(),
});

export const fundraiserMarketingSchema = Yup.object({
  logo: Yup.string().required("Logo is required"),
  aboutUs: Yup.string().required("Description is required"),
  banner: Yup.string().required("Banner is required"),
});

export const fundraiserGettingStartedSchema = Yup.object().shape({
  fundraiserName: Yup.string()
    .matches(/^[\p{L}0-9\s&'().,\-@#$%!?/:;+=_*[\]{}|~`"\\]+$/u, "Fundraiser Name can contain letters, numbers, spaces, and special characters")
    .max(70, "Fundraiser Name cannot be more than 70 characters")
    .required("Fundraiser Name is required"),
  fundraiserAddress: Yup.string().required("Fundraiser Address is required"),
  logo: Yup.string(), // Logo is optional
  websiteUrl: Yup.string()
    .matches(
      /^(https?:\/\/)?(www\.)?[\w-]+(\.[\w-]+)+.*$/,
      "Invalid website URL"
    )
    .required("Website URL is required"),
  fundraiserSubCategory: Yup.array().when('fundraiserCategory', {
    is: (val) => val !== 'Community Organisations',
    then: () => Yup.array()
      .of(Yup.string().required("Fundraiser SubCategories are required"))
      .min(1, "Atleast Select one Fundraiser Subcategory"),
    otherwise: () => Yup.array().optional(),
  }),
  fundraiserCategory: Yup.string().required("Fundraiser Category is required."),
  personName: Yup.string()
    .matches(/^[A-Za-z0-9\s]+$/, "Contact Person Name can contain letters, numbers, and spaces")
    .max(25, "Contact Person Name cannot be more than 25 characters")
    .required("Contact Person Name is required"),
  ...emailSchema,
  personContact: Yup.string().required("Contact is required"),
  aboutOrganization: Yup.string()
    .required("Organisation Information is required")
    .max(500, "Organisation Information cannot be more than 500 characters"),
  reasons: Yup.array()
    .of(Yup.string().required("Reasons are required"))
    .min(1, "Atleast Select one Reason"),
});

export const rejectedReasonSchema = Yup.object().shape({
  reason: Yup.string().required("Reason is required"),
});

export const redemptionSchema = Yup.object().shape({
  code: Yup.string()
    .trim()
    .matches(/^[a-zA-Z0-9]{6}$/, "Redemption code must be exactly 6 alphanumeric characters")
    .required("Code is required"),
});

export const merchantProfileSchema = Yup.object().shape({
  businessName: Yup.string()
    .max(50, "Business Name cannot be more than 50 characters")
    .required("Business Name is required"),
  email: Yup.string()
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address")
    .required("Email is required"),
  contact: Yup.string().required("Contact is required"),
  address: Yup.string().required("Location is required"),
  websiteUrl: Yup.string()
    .matches(
      /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}(\/[^\s]*)?$/,
      "Invalid website URL"
    )
    .required("Website URL is required"),
  personContact: Yup.string().required("Contact Person Name is required"),
  aboutBusiness: Yup.string()
    .required("Business Information is required")
    .max(500, "Business Information cannot be more than 500 characters"),
  logo: Yup.string(),
  // New organisation fields (optional for general use, required for fundraisers)
  organisationName: Yup.string()
    .max(100, "Organisation Name cannot be more than 100 characters")
    .optional(),
  abn: Yup.string()
    .matches(/^[0-9]{11}$/, "ABN must be an 11-digit number")
    .optional(),
  charityRegistrationNumber: Yup.string()
    .max(50, "Charity Registration Number is too long")
    .optional(),
  accountsEmail: Yup.string()
    .email("Invalid email address")
    .optional(),
  // Fundraiser-specific fields
  fundraiserCause: Yup.array().optional(),
  fundraiserCategory: Yup.string().optional(),
  fundraiserSubCategory: Yup.array().optional(),
});

// Relaxed schema for editing profiles: validates format if provided, but doesn't require fields
export const editProfileSchema = Yup.object().shape({
  businessName: Yup.string()
    .max(50, "Business Name cannot be more than 50 characters")
    .required("Business Name is required"),
  email: Yup.string().test(
    "is-email",
    "Invalid email address",
    (val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)
  ),
  contact: Yup.string().required("Contact Number is required"),
  address: Yup.string().required("Location is required"),
  websiteUrl: Yup.string()
    .matches(
      /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}(\/[^\s]*)?$/,
      "Invalid website URL"
    )
    .required("Website URL is required"),
  personContact: Yup.string(),
  aboutBusiness: Yup.string()
    .max(500, "Business Information cannot be more than 500 characters")
    .required("Business description is required"),
  logo: Yup.string(),
  organisationName: Yup.string().max(100, "Organisation Name cannot be more than 100 characters"),
  abn: Yup.string().test(
    "abn-format",
    "ABN must be an 11-digit number",
    (val) => !val || /^[0-9]{11}$/.test(val)
  ),
  charityRegistrationNumber: Yup.string().max(50, "Charity Registration Number is too long"),
  accountsEmail: Yup.string().test(
    "is-accounts-email",
    "Invalid email address",
    (val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)
  ),
  fundraiserCause: Yup.array().optional(),
  fundraiserCategory: Yup.string().optional(),
  fundraiserSubCategory: Yup.array().optional(),
});

export const fundraiserEditProfileSchema = editProfileSchema.shape({
  contact: Yup.string().required("Contact Number is required"),
  personContact: Yup.string().required("Contact Person Name is required"),
  organisationName: Yup.string().required("Organisation Name is required").max(100, "Organisation Name cannot be more than 100 characters"),
  abn: Yup.string()
    .required("ABN is required")
    .matches(/^[0-9]{11}$/, "ABN must be an 11-digit number"),
  charityRegistrationNumber: Yup.string().required("Charity Registration Number is required").max(50, "Charity Registration Number is too long"),
  accountsEmail: Yup.string()
    .required("Accounts Email is required")
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address"),
});